import { type FunctionComponent, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Text } from '@atlaskit/primitives';
import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';
import { fg, featureGateKeys } from '@app/feature-gates';

const messages = defineMessages({
  betaTitle: {
    id: 'app.updates.beta.banner.title',
    defaultMessage: 'This feature is in beta',
    description: 'Title for the beta banner on the app updates list page',
  },
  betaBody: {
    id: 'app.updates.beta.banner.body',
    defaultMessage:
      "What's new across Atlassian is currently in beta. Browse release notes for Atlassian changes affecting your organization and track their rollout statuses. More features are on the way — including a dedicated view for reviewing Release tracks bundled releases.",
    description: 'Body text for the beta banner on the app updates list page',
  },
  betaLink: {
    id: 'app.updates.beta.banner.link',
    defaultMessage: 'Share feedback on Community',
    description: 'Link text for the beta banner on the app updates list page',
  },
  launchTitle: {
    id: 'app.updates.launch.banner.title',
    defaultMessage: 'New ways to stay on top of release notes',
    description: 'Title for the launch banner on the app updates list page',
  },
  launchBody: {
    id: 'app.updates.launch.banner.body',
    defaultMessage:
      'Quickly catch up on recent changes with the <strong>New this week</strong> filter and unread indicators, or switch to <strong>Release tracks changes</strong> to review what’s included in upcoming Preview and Bundled releases.',
    description: 'Body text for the launch banner on the app updates list page',
  },
  launchLink: {
    id: 'app.updates.launch.banner.link',
    defaultMessage: 'Learn more on Community',
    description: 'Link text for the launch banner on the app updates list page',
  },
});

const COMMUNITY_FEEDBACK_URL =
  'https://community.atlassian.com/forums/Community-Announcements-articles/x/ba-p/3226637';

const RELEASE_NOTES_LAUNCH_POST_URL =
  'https://community.atlassian.com/forums/Community-Announcements-articles/Two-new-ways-to-stay-on-top-of-Atlassian-Cloud-release-notes/ba-p/3252956';

export const BetaBanner: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const showLaunchCopy = fg(featureGateKeys.gaReleaseChanges);

  const title = showLaunchCopy ? messages.launchTitle : messages.betaTitle;
  const body = showLaunchCopy ? messages.launchBody : messages.betaBody;
  const link = showLaunchCopy ? messages.launchLink : messages.betaLink;
  const href = showLaunchCopy
    ? RELEASE_NOTES_LAUNCH_POST_URL
    : COMMUNITY_FEEDBACK_URL;

  return (
    <SectionMessage
      appearance="discovery"
      title={formatMessage(title)}
      actions={[
        <SectionMessageAction href={href} target="_blank">
          {formatMessage(link)}
        </SectionMessageAction>,
      ]}
    >
      <Text>
        {formatMessage(body, {
          strong: (chunks: ReactNode) => (
            <Text as="span" weight="bold">
              {chunks}
            </Text>
          ),
        })}
      </Text>
    </SectionMessage>
  );
};

export default BetaBanner;
